import { AnimatePresence, m } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EASE } from "../Motion";
import { Icon } from "../Icon";
import { createCanvasRenderer } from "./canvasRenderer";
import * as D from "./dimensions";
import { STEP, Spin, advance, launch, step } from "./physics";
import { SceneState, WheelRenderer } from "./scene";
import { createWebGpuRenderer } from "./webgpuRenderer";
import { RosterMember, WheelEntry, buildWheelLayout } from "./wheelLayout";

// The wheel as a component: a canvas, whatever renderer the device can give
// us, and one animation loop reading its state out of refs. Nothing about the
// spin lives in React state except the two things the DOM around the canvas
// actually shows — which phase we're in, and who won — so a frame never costs
// a re-render.

/** How fast the wheel idles before it is ever spun, in radians a second. */
const IDLE_SPEED = 0.17;
/** Seconds for the winner's glow to come up, and how far its plate grows. */
const GLOW_RISE = 0.5;
/** Barely: the plate is painted in the pocket, and a pocket is only so wide. */
const WINNER_SCALE = 1.06;

type Phase = "idle" | "spinning" | "settled";

/**
 * The simulation works in x/y/z because that is what colliding with things is
 * written in; the renderers want the ball where it is on the wheel. Same
 * position, said the other way round.
 */
function readSimulation(simulation: Spin, state: SceneState) {
  const { x, y, z } = simulation.ball;
  state.wheelAngle = simulation.wheelAngle;
  state.ball = { angle: Math.atan2(z, x), radius: Math.hypot(x, z), y };
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

export default function Roulette({
  members,
  onSettled,
}: {
  members: RosterMember[];
  onSettled?: (entry: WheelEntry) => void;
}) {
  const layout = useMemo(() => buildWheelLayout(members), [members]);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<WheelRenderer | null>(null);

  const [kind, setKind] = useState<"pending" | "webgpu" | "canvas" | "none">("pending");
  const [phase, setPhase] = useState<Phase>("idle");
  const [winnerSlot, setWinnerSlot] = useState<number | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  // Everything the animation loop touches, kept out of React's hands.
  const phaseRef = useRef<Phase>("idle");
  const simulationRef = useRef<Spin | null>(null);
  /** Time left over from the last frame, carried into the next fixed step. */
  const carryRef = useRef(0);
  const scene = useRef<SceneState>({
    wheelAngle: 0,
    ball: { angle: 0, radius: D.BALL_TRACK_RADIUS, y: D.bowlSurfaceY(D.BALL_TRACK_RADIUS) },
    ballVisible: false,
    highlight: -1,
    glow: 0,
    highlightScale: 1,
    time: 0,
  });
  const sizeRef = useRef({ width: 0, height: 0, dpr: 1 });
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  // Build the renderer for this roster: WebGPU where the device has it, the
  // 2D painter where it doesn't.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let disposed = false;

    (async () => {
      const gpu = await createWebGpuRenderer(canvas, layout);
      if (disposed) {
        gpu?.destroy();
        return;
      }
      const renderer = gpu ?? createCanvasRenderer(canvas, layout);
      rendererRef.current = renderer;
      setKind(renderer ? renderer.kind : "none");
      // The observer has almost certainly already measured the box by now, so
      // hand the size straight over rather than waiting for it to change.
      const { width, height, dpr } = sizeRef.current;
      if (renderer && width > 0) renderer.resize(width, height, dpr);
    })();

    return () => {
      disposed = true;
      rendererRef.current?.destroy();
      rendererRef.current = null;
    };
  }, [layout]);

  // Track the box. The loop applies the size, so a resize arriving before the
  // renderer exists is simply picked up on the first frame after it does.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect || rect.width < 8 || rect.height < 8) return;
      sizeRef.current = {
        width: rect.width,
        height: rect.height,
        // Capped: a wheel at 3× on a modern phone costs a lot of fill rate for
        // a difference nobody can see at this size.
        dpr: Math.min(window.devicePixelRatio || 1, 2),
      };
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // The one animation loop, started on mount and never restarted.
  useEffect(() => {
    let frame = 0;
    let last = performance.now();
    const started = last;
    let applied = { width: 0, height: 0, dpr: 0 };

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;

      const renderer = rendererRef.current;
      if (!renderer) return;

      const size = sizeRef.current;
      if (
        size.width > 0 &&
        (size.width !== applied.width || size.height !== applied.height || size.dpr !== applied.dpr)
      ) {
        renderer.resize(size.width, size.height, size.dpr);
        applied = { ...size };
      }

      const state = scene.current;
      state.time = (now - started) / 1000;

      const simulation = simulationRef.current;
      if (simulation) {
        // The world runs on its own fixed timestep whatever the frame rate is;
        // this only hands it the elapsed time and reads the answer back.
        carryRef.current = advance(simulation, dt, carryRef.current);
        readSimulation(simulation, state);
        if (simulation.settledSlot >= 0 && phaseRef.current === "spinning") {
          phaseRef.current = "settled";
          state.highlight = simulation.settledSlot;
          setPhase("settled");
          setWinnerSlot(simulation.settledSlot);
          onSettledRef.current?.(layout.entries[layout.slots[simulation.settledSlot]]);
        }
      } else {
        // Turning gently on its own, so the tab doesn't open on a still image.
        state.wheelAngle += IDLE_SPEED * dt;
      }

      // The winner's glow comes up over half a second and then breathes.
      const target = state.highlight >= 0 ? 1 : 0;
      const step = dt / GLOW_RISE;
      const level = Math.max(0, Math.min(1, state.glow + (target > state.glow ? step : -step * 2)));
      state.glow = level * (0.82 + 0.18 * Math.sin(state.time * 2.6));
      state.highlightScale = 1 + (WINNER_SCALE - 1) * level;

      renderer.render(state);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [layout]);

  const spin = useCallback(() => {
    // Nothing to spin before a renderer has come up: the loop would start the
    // throw but never advance it, leaving the button stuck on "ruller…".
    if (phaseRef.current === "spinning" || !rendererRef.current) return;
    const state = scene.current;
    const simulation = launch({ wheelAngle: state.wheelAngle });
    simulationRef.current = simulation;
    carryRef.current = 0;
    state.highlight = -1;
    state.ballVisible = true;
    setWinnerSlot(null);

    if (reducedMotion) {
      // Asked for less motion: run the same throw to its end straight away and
      // show where it finished. The spin is the animation — there is no
      // shorter version of it that is still the same spin — so the honest
      // answer is to skip the watching, not to fake a quicker ball.
      while (simulation.settledSlot < 0) step(simulation, STEP);
      readSimulation(simulation, state);
      phaseRef.current = "settled";
      state.highlight = simulation.settledSlot;
      setPhase("settled");
      setWinnerSlot(simulation.settledSlot);
      onSettledRef.current?.(layout.entries[layout.slots[simulation.settledSlot]]);
      return;
    }

    phaseRef.current = "spinning";
    setPhase("spinning");
  }, [layout, reducedMotion]);

  const winner = winnerSlot === null ? null : layout.entries[layout.slots[winnerSlot]];

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* The wheel is fitted to the width of the box, because it has to fit
          across however narrow the phone is. Left to fill a tall column as
          well, that leaves a stretch of empty felt above and below it, so the
          box is given a shape of its own: as tall as the wheel wants, and no
          taller than there is room for. */}
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2.5">
        <div
          ref={containerRef}
          onPointerDown={spin}
          role="button"
          aria-label="Drej rouletten"
          className="relative w-full aspect-[7/8] max-h-full rounded-lg overflow-hidden border border-line-soft bg-ink cursor-pointer select-none"
        >
          <canvas ref={canvasRef} className="absolute inset-0 block" />

          {kind === "none" && (
            <div className="absolute inset-0 flex items-center justify-center px-8 text-center font-mono text-[11px] text-paper/70">
              Hjulet kunne ikke tegnes på denne enhed.
            </div>
          )}


          <AnimatePresence>
            {phase === "idle" && (
              <m.div
                key="hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.35, duration: 0.4, ease: EASE }}
                className="absolute inset-x-0 top-2.5 flex justify-center pointer-events-none"
              >
                <span className="font-mono text-[9px] tracking-[1.4px] uppercase text-paper/75 bg-ink/45 px-2.5 py-1 rounded-full backdrop-blur-sm">
                  Tryk for at dreje
                </span>
              </m.div>
            )}
          </AnimatePresence>
        </div>

        {/* The result, and what stands in its place until there is one. A
            fixed height for both: announcing the winner shouldn't resize the
            wheel above it. It sits under the wheel rather than over it — laid
            across the bottom of the canvas it covered up the very pocket it
            was naming, about a third of the time. */}
        <div className="w-full h-[58px] flex items-center justify-center flex-shrink-0">
          <AnimatePresence mode="wait" initial={false}>
            {winner ? (
              <m.div
                key="winner"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="w-full flex items-center gap-3 rounded-lg bg-ink text-paper px-3.5 py-2.5"
              >
                <div className="text-[26px] leading-none">{winner.emoji}</div>
                <div className="min-w-0">
                  <div className="font-mono text-[8px] tracking-[1.5px] uppercase text-paper/55">
                    Kuglen landede på
                  </div>
                  <div className="font-display text-[19px] leading-[1.1] font-extrabold uppercase tracking-tight truncate">
                    {winner.kind === "beer" ? "Øl" : winner.label}
                  </div>
                </div>
                {winner.kind === "beer" && (
                  <div className="ml-auto text-right font-mono text-[8.5px] leading-[1.35] tracking-[1.2px] uppercase text-brass flex-shrink-0">
                    En omgang
                    <br />
                    til bordet
                  </div>
                )}
              </m.div>
            ) : (
              <m.div
                key="hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="font-mono text-[9.5px] tracking-[1.2px] uppercase text-ink3 text-center"
              >
                Tre felter til hver · ét felt er en øl
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* The wheel's key: every face on it, how many of the 22 fields it
          holds, and — once the ball has stopped — which one it found. */}
      <div className="flex gap-1 mt-2 flex-shrink-0">
        {layout.entries.map((entry) => {
          const won = winner?.key === entry.key;
          return (
            <m.div
              key={entry.key}
              animate={won ? { scale: [1, 1.1, 1.04] } : { scale: 1 }}
              transition={{ duration: 0.45, ease: EASE }}
              className={`flex-1 min-w-0 flex flex-col items-center gap-0.5 py-1.5 rounded border ${
                won ? "bg-ink text-paper border-ink" : "border-line-soft text-ink3"
              }`}
            >
              <div className="text-base leading-none">{entry.emoji}</div>
              <div className="font-mono text-[8px] tracking-[0.8px] uppercase truncate max-w-full px-0.5">
                {entry.kind === "beer" ? "Øl" : entry.label}
              </div>
            </m.div>
          );
        })}
      </div>

      <div className="mt-2 flex-shrink-0">
        <button
          type="button"
          onClick={spin}
          // Nothing to spin if neither renderer came up: the wheel is the game.
          disabled={phase === "spinning" || kind === "none"}
          className="w-full border-0 py-3.5 px-[18px] rounded-lg bg-ink text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-between gap-3 disabled:opacity-45"
        >
          <span>
            {phase === "spinning" ? "Kuglen ruller…" : phase === "settled" ? "Drej igen" : "Drej hjulet"}
          </span>
          <Icon name="dice" size={13} className="opacity-65" />
        </button>
      </div>
    </div>
  );
}
